import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import LoadingStars from './LoadingStars'

// ─────────────────────────────────────────────────────────────
//  EMBROIDERY CLEANUP REVIEW
//  Sits between upload and stitch generation. Shows the user what the
//  cleanup pipeline detected — cropped design region, consolidated thread
//  colors, dropped noise/bleed — and lets them adjust before committing.
//
//  The Wilcom-style "Auto Fabric Assistant" pattern: trust the algorithm
//  but let the human override anything wrong before stitches are made.
// ─────────────────────────────────────────────────────────────

const TOOL = {
  CONFIRM: 'confirm',  // default — view only
  ERASE:   'erase',    // paint white to remove regions
  PAINT:   'paint',    // sample original pixel color and re-add
}

// Brush presets — sized in source-image pixels. Labels include the
// approximate physical size at the design's typical 100mm-wide stitch
// output, so the user can pick a brush with intent. Earlier UI only
// offered small/med/large which gave no idea what those meant on
// finished embroidery.
const BRUSH_PRESETS = [
  { id: 'tiny',   px: 4,   label: 'tiny'   },
  { id: 'small',  px: 10,  label: 'small'  },
  { id: 'medium', px: 22,  label: 'medium' },
  { id: 'large',  px: 50,  label: 'large'  },
  { id: 'huge',   px: 100, label: 'huge'   },
]

const API_URL = import.meta.env.VITE_API_URL || ''

export default function EmbroideryCleanupReview ({
  cleanedImageBase64,    // string — base64 PNG, the cleaned design from /analyze-and-clean
  originalImageUrl,      // string — for "show original" comparison + paint-back sampling
  cropBbox,              // [x0, y0, x1, y1] in original-image pixel coordinates
  cleanedSize,           // [w, h] of cleaned image (matches crop dimensions)
  detectedColors,        // [{hex, rgb: [r,g,b]}, ...]
  detectedTextRegions,   // [{text, bbox_px, font_guess, color_hex}, ...]
  decisions,             // [string, ...] — pipeline log for "what happened" expandable
  onConfirm,             // (finalImageBase64, finalColors[], fabricKey, textFontOverrides) → void
  onCancel,              // () → void
  isProcessing,          // boolean — true while we're awaiting digitization
  onResimplify,          // (level: 'off'|'light'|'medium'|'heavy') => void — re-runs cleanup with new simplify level
  currentSimplifyLevel,  // string — current level used for this cleanup (so the picker reflects state)
  isHandPattern,         // boolean — flagged by clean_image.py if foreground is dominated by parallel scribble
  handPatternReason,     // string — human-readable reason for the flag
}) {
  const canvasRef = useRef(null)
  const originalImgRef = useRef(null)
  const cleanedImgRef = useRef(null)
  // Current pixel state of the cleaned canvas (mutated by erase/paint tools)
  const [canvasReady, setCanvasReady] = useState(false)
  const [tool, setTool] = useState(TOOL.CONFIRM)
  const [brushSize, setBrushSize] = useState(BRUSH_PRESETS[1].px)
  const [activeColor, setActiveColor] = useState(null) // hex for paint tool
  const [removedColors, setRemovedColors] = useState(new Set()) // hexes hidden via chip-X
  // Map<originalHex, newHex> — user-driven thread color substitutions.
  // Pixels matching the original detected color are repainted with the
  // chosen replacement on canvas. This is what powers the "swap pewter
  // for navy" workflow.
  const [swappedColors, setSwappedColors] = useState({})
  const [pickerOpenFor, setPickerOpenFor] = useState(null) // hex of chip whose picker is open
  const [showOriginal, setShowOriginal] = useState(false)
  const [showDecisions, setShowDecisions] = useState(false)
  const [zoom, setZoom] = useState(1)
  // Track whether the user has manually edited pixels (for the warning banner)
  const [hasEdits, setHasEdits] = useState(false)
  // Auto Fabric Assistant state
  const [fabrics, setFabrics] = useState([])
  const [selectedFabric, setSelectedFabric] = useState('cotton')
  const [showFabricAdvice, setShowFabricAdvice] = useState(false)

  // Thread library matching state
  const [threadBrands, setThreadBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState('brother')
  const [threadMatches, setThreadMatches] = useState([])  // parallel to detectedColors
  // Font preview thumbnails (for the per-text-region font picker)
  const [fontPreviews, setFontPreviews] = useState([])
  // Per-region font selection (initialized to font_guess from detection)
  const [textFontOverrides, setTextFontOverrides] = useState({})
  useEffect(() => {
    if (!detectedTextRegions || detectedTextRegions.length === 0) return
    const initial = {}
    for (const r of detectedTextRegions) initial[r.text] = r.font_guess || 'block'
    setTextFontOverrides(initial)
  }, [detectedTextRegions])

  // Load fabric presets + thread brands once
  useEffect(() => {
    fetch(`${API_URL}/export/fabrics`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('fabrics fetch failed')))
      .then(d => setFabrics(d.fabrics || []))
      .catch(err => console.warn('fabric list load:', err))
    fetch(`${API_URL}/export/thread-brands`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('brands fetch failed')))
      .then(d => setThreadBrands(d.brands || []))
      .catch(err => console.warn('thread brands load:', err))
    fetch(`${API_URL}/export/font-previews`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('font preview fetch failed')))
      .then(d => setFontPreviews(d.fonts || []))
      .catch(err => console.warn('font previews:', err))
  }, [])

  // Re-match threads whenever the brand or detected colors change
  useEffect(() => {
    if (!detectedColors || detectedColors.length === 0) { setThreadMatches([]); return }
    fetch(`${API_URL}/export/match-threads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hexes: detectedColors.map(c => c.hex), brand: selectedBrand }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('match failed')))
      .then(d => setThreadMatches(d.matches || []))
      .catch(err => console.warn('thread match:', err))
  }, [detectedColors, selectedBrand])

  const activeFabric = fabrics.find(f => f.key === selectedFabric)

  // Compute view-fit zoom on first load
  useEffect(() => {
    if (!cleanedSize) return
    const [cw, ch] = cleanedSize
    if (cw < 1 || ch < 1) return
    // Fit into 80vh × 70vw with some padding
    const maxW = window.innerWidth * 0.55
    const maxH = window.innerHeight * 0.65
    setZoom(Math.min(maxW / cw, maxH / ch, 4))
  }, [cleanedSize])

  // Load images and initialize canvas once
  useEffect(() => {
    if (!cleanedImageBase64) return
    const img = new Image()
    img.onload = () => {
      cleanedImgRef.current = img
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      setCanvasReady(true)
    }
    img.src = `data:image/png;base64,${cleanedImageBase64}`
  }, [cleanedImageBase64])

  // Load original image for paint-back sampling
  useEffect(() => {
    if (!originalImageUrl) return
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => { originalImgRef.current = img }
    img.src = originalImageUrl
  }, [originalImageUrl])

  // Re-snap a hidden color to white whenever the user toggles a color off
  // (and restore it when toggled back on, by re-drawing from base + applying
  // the user's manual paint deltas — for simplicity we only support a one-way
  // "remove" via chips with confirmation; restore re-runs the cleanup).
  useEffect(() => {
    if (!canvasReady || !cleanedImgRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    // Repaint base from original cleaned image
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(cleanedImgRef.current, 0, 0)
    // Apply removed-color masking AND color swaps in a single pixel pass.
    const swapEntries = Object.entries(swappedColors)
    if (removedColors.size > 0 || swapEntries.length > 0) {
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imgData.data
      const removeRgbs = []
      for (const hex of removedColors) {
        const c = detectedColors.find(d => d.hex === hex)
        if (c) removeRgbs.push(c.rgb)
      }
      // Build swap rules: [{ from: [r,g,b], to: [r,g,b] }, ...]
      const swapRules = []
      for (const [fromHex, toHex] of swapEntries) {
        const c = detectedColors.find(d => d.hex === fromHex)
        if (!c) continue
        const t = toHex.replace('#', '')
        const tr = parseInt(t.slice(0, 2), 16)
        const tg = parseInt(t.slice(2, 4), 16)
        const tb = parseInt(t.slice(4, 6), 16)
        if (isNaN(tr) || isNaN(tg) || isNaN(tb)) continue
        swapRules.push({ from: c.rgb, to: [tr, tg, tb] })
      }
      const tol = 20
      for (let i = 0; i < data.length; i += 4) {
        // Removes win first (white wash). Then swaps.
        let handled = false
        for (const [r, g, b] of removeRgbs) {
          if (Math.abs(data[i] - r) < tol &&
              Math.abs(data[i + 1] - g) < tol &&
              Math.abs(data[i + 2] - b) < tol) {
            data[i] = data[i + 1] = data[i + 2] = 255
            handled = true
            break
          }
        }
        if (handled) continue
        for (const rule of swapRules) {
          const [r, g, b] = rule.from
          if (Math.abs(data[i] - r) < tol &&
              Math.abs(data[i + 1] - g) < tol &&
              Math.abs(data[i + 2] - b) < tol) {
            data[i]     = rule.to[0]
            data[i + 1] = rule.to[1]
            data[i + 2] = rule.to[2]
            break
          }
        }
      }
      ctx.putImageData(imgData, 0, 0)
    }
    setHasEdits(false)
  }, [canvasReady, removedColors, detectedColors, swappedColors])

  // ── Tool handlers ───────────────────────────────────────────────
  const canvasMouseToImage = useCallback((e) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width * canvas.width
    const y = (e.clientY - rect.top) / rect.height * canvas.height
    return { x: Math.floor(x), y: Math.floor(y) }
  }, [])

  const applyBrush = useCallback((cx, cy) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const radius = brushSize / 2
    if (tool === TOOL.ERASE) {
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      ctx.fill()
      setHasEdits(true)
    } else if (tool === TOOL.PAINT && activeColor && originalImgRef.current) {
      // Sample the equivalent pixel from the original image (translated by cropBbox)
      const orig = originalImgRef.current
      const [ox0, oy0, ox1, oy1] = cropBbox || [0, 0, orig.naturalWidth, orig.naturalHeight]
      const scaleX = (ox1 - ox0) / canvas.width
      const scaleY = (oy1 - oy0) / canvas.height
      const origX = Math.floor(ox0 + cx * scaleX)
      const origY = Math.floor(oy0 + cy * scaleY)
      // Read the source pixel from the original via an offscreen canvas
      const off = document.createElement('canvas')
      off.width = orig.naturalWidth
      off.height = orig.naturalHeight
      const offCtx = off.getContext('2d')
      offCtx.drawImage(orig, 0, 0)
      try {
        const px = offCtx.getImageData(origX, origY, 1, 1).data
        // Snap to nearest active color (default to activeColor unless original is closer)
        ctx.fillStyle = `rgb(${px[0]},${px[1]},${px[2]})`
      } catch {
        ctx.fillStyle = activeColor
      }
      ctx.beginPath()
      ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      ctx.fill()
      setHasEdits(true)
    }
  }, [tool, brushSize, activeColor, cropBbox])

  const [isPainting, setIsPainting] = useState(false)
  const handleMouseDown = (e) => {
    if (tool === TOOL.CONFIRM) return
    const p = canvasMouseToImage(e)
    if (!p) return
    setIsPainting(true)
    applyBrush(p.x, p.y)
  }
  const handleMouseMove = (e) => {
    if (!isPainting) return
    const p = canvasMouseToImage(e)
    if (!p) return
    applyBrush(p.x, p.y)
  }
  const handleMouseUp = () => setIsPainting(false)

  // ── Color chip controls ─────────────────────────────────────────
  // visibleColors reflects: detected colors minus removed, with any user
  // swaps applied (so downstream consumers see the swap result, not the
  // original detected hex).
  const visibleColors = useMemo(
    () => (detectedColors || [])
      .filter(c => !removedColors.has(c.hex))
      .map(c => {
        const replacement = swappedColors[c.hex]
        if (!replacement) return c
        const t = replacement.replace('#', '')
        const tr = parseInt(t.slice(0, 2), 16)
        const tg = parseInt(t.slice(2, 4), 16)
        const tb = parseInt(t.slice(4, 6), 16)
        if (isNaN(tr) || isNaN(tg) || isNaN(tb)) return c
        return { ...c, hex: replacement.toLowerCase(), rgb: [tr, tg, tb], swappedFrom: c.hex }
      }),
    [detectedColors, removedColors, swappedColors]
  )

  const removeColor = (hex) => {
    setRemovedColors(prev => {
      const n = new Set(prev)
      n.add(hex)
      return n
    })
    if (activeColor === hex) setActiveColor(null)
  }

  const restoreColor = (hex) => {
    setRemovedColors(prev => {
      const n = new Set(prev)
      n.delete(hex)
      return n
    })
  }

  const swapColor = (originalHex, newHex) => {
    setSwappedColors(prev => {
      const n = { ...prev }
      // Sanity-clamp the hex to a 6-digit form
      const clean = (newHex || '').replace(/[^#0-9a-fA-F]/g, '')
      if (!clean.startsWith('#') || clean.length !== 7) return n
      n[originalHex] = clean.toLowerCase()
      return n
    })
    setPickerOpenFor(null)
  }

  const clearSwap = (originalHex) => {
    setSwappedColors(prev => {
      const n = { ...prev }
      delete n[originalHex]
      return n
    })
  }

  // ── Confirm: flatten canvas → base64 → upstream ─────────────────
  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dataUrl = canvas.toDataURL('image/png')
    const base64 = dataUrl.split(',')[1]
    onConfirm?.(base64, visibleColors, selectedFabric, textFontOverrides)
  }, [visibleColors, selectedFabric, textFontOverrides, onConfirm])

  // ── Render ──────────────────────────────────────────────────────
  if (!cleanedImageBase64) return null

  const cw = cleanedSize ? cleanedSize[0] : 800
  const ch = cleanedSize ? cleanedSize[1] : 600
  const dispW = Math.round(cw * zoom)
  const dispH = Math.round(ch * zoom)

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col w-full max-w-7xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              Cleanup Review
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Confirm the cleaned design before generating stitches.
              Adjust colors, crop, or paint corrections as needed.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-40 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Hand-embroidery pattern warning. Surfaces when clean_image.py
            detects a parallel-scribble dominated foreground (sashiko,
            cross-stitch chart, hand-thread-direction art). Machine
            output won't reproduce the cross-hatched fill texture, so we
            warn before the user spends digitization time on it. */}
        {isHandPattern && (
          <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-300 dark:border-amber-500/50 px-5 py-2.5 flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-800 dark:text-amber-200 mb-0.5">
                This looks like a HAND embroidery pattern, not a machine design
              </p>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                {handPatternReason || 'Foreground is dominated by parallel scribble strokes.'}
                {' '}Machine output won't reproduce the cross-hatched fill texture
                — the result will be solid filled silhouettes with the texture lost.
                You can still proceed (we'll do our best), or load a design built
                for machine embroidery instead.
              </p>
            </div>
          </div>
        )}

        {/* Body: canvas + sidebar */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: canvas */}
          <div className="relative flex-1 flex flex-col bg-slate-100 dark:bg-slate-800/50 overflow-auto">
            {/* While the digitizer is generating stitches behind the
                scenes, drift shooting stars across the preview area so
                the user knows something is happening (not frozen). */}
            {isProcessing && (
              <LoadingStars density={20} label="Stitching it up…" />
            )}
            <div className="flex-1 flex items-center justify-center p-6 min-h-0">
              <div
                className="relative"
                style={{ width: dispW, height: dispH }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Show original beneath as a swap when user toggles */}
                {showOriginal && originalImageUrl && (
                  <img
                    src={originalImageUrl}
                    alt="Original"
                    className="absolute inset-0 w-full h-full object-contain bg-white"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
                {!showOriginal && (
                  <canvas
                    ref={canvasRef}
                    className="block bg-white"
                    style={{
                      width: '100%',
                      height: '100%',
                      cursor: tool === TOOL.ERASE ? 'crosshair'
                            : tool === TOOL.PAINT ? 'crosshair'
                            : 'default',
                      imageRendering: zoom > 2 ? 'pixelated' : 'auto',
                    }}
                  />
                )}
              </div>
            </div>

            {/* Tool bar */}
            <div className="flex items-center gap-2 px-5 py-2 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <ToolButton active={tool === TOOL.CONFIRM} onClick={() => setTool(TOOL.CONFIRM)} label="View" icon="👁" />
              <ToolButton active={tool === TOOL.ERASE}   onClick={() => setTool(TOOL.ERASE)}   label="Erase" icon="✕" />
              <ToolButton active={tool === TOOL.PAINT}   onClick={() => setTool(TOOL.PAINT)}   label="Paint back" icon="✎" disabled={!activeColor} />

              {(tool === TOOL.ERASE || tool === TOOL.PAINT) && (
                <div className="flex items-center gap-1.5 ml-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">Brush</span>
                  {BRUSH_PRESETS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setBrushSize(p.px)}
                      title={`${p.label} brush — ${p.px}px diameter`}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        brushSize === p.px
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">
                    {brushSize}px
                  </span>
                </div>
              )}

              <div className="flex-1" />

              <button
                onClick={() => setShowOriginal(s => !s)}
                className="px-2.5 py-1 text-xs rounded transition-colors bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"
              >
                {showOriginal ? '👁 Cleaned' : '👁 Original'}
              </button>

              <span className="text-[10px] text-slate-500 ml-2">
                {cw}×{ch}px · {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom(z => Math.max(0.25, z * 0.8))}
                className="px-2 py-1 text-xs rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"
              >−</button>
              <button
                onClick={() => setZoom(z => Math.min(8, z * 1.25))}
                className="px-2 py-1 text-xs rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"
              >+</button>
            </div>
          </div>

          {/* Right: fabric + color chips + decisions */}
          <div className="w-72 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col overflow-hidden">
            <div className="p-4 overflow-y-auto flex-1">
              {/* Auto Fabric Assistant */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Fabric
                  </h3>
                  {activeFabric && (
                    <button
                      onClick={() => setShowFabricAdvice(s => !s)}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
                      title="Show Zenna's advice for this fabric"
                    >
                      {showFabricAdvice ? '▼' : '?'} Advice
                    </button>
                  )}
                </div>
                <select
                  value={selectedFabric}
                  onChange={e => setSelectedFabric(e.target.value)}
                  className="w-full px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200"
                >
                  {fabrics.map(f => (
                    <option key={f.key} value={f.key}>{f.name}</option>
                  ))}
                </select>
                {activeFabric && (
                  <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                    <div
                      className="w-3 h-3 rounded-sm border border-slate-300 dark:border-slate-600 flex-shrink-0"
                      style={{ backgroundColor: activeFabric.swatch_color }}
                    />
                    <span className="truncate">
                      {activeFabric.category} · {activeFabric.stretch} stretch
                    </span>
                  </div>
                )}
                {showFabricAdvice && activeFabric && (
                  <div className="mt-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 space-y-1.5">
                    <p className="text-[11px] text-slate-700 dark:text-slate-200 leading-relaxed">
                      {activeFabric.advice}
                    </p>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 pt-1.5 border-t border-amber-200 dark:border-amber-500/20 space-y-0.5">
                      <div><strong>Stabilizer:</strong> {activeFabric.stabilizer}</div>
                      <div><strong>Needle:</strong> {activeFabric.needle}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto-simplify — drops sub-threshold components and merges
                  close ones. Default 'light' drops sub-0.5mm² dust only.
                  Pile fabrics + dense line-art benefit from medium/heavy. */}
              {onResimplify && (
                <div className="mb-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Auto-simplify
                  </h3>
                  <div className="grid grid-cols-4 gap-1">
                    {[
                      { id: 'off',    label: 'Off',    desc: 'Keep all detail' },
                      { id: 'light',  label: 'Light',  desc: 'Drop dust < 0.5mm²' },
                      { id: 'medium', label: 'Medium', desc: 'Merge gaps ≤ 0.4mm' },
                      { id: 'heavy',  label: 'Heavy',  desc: 'Bold output, pile-safe' },
                    ].map(opt => {
                      const isActive = (currentSimplifyLevel || 'light') === opt.id
                      return (
                        <button
                          key={opt.id}
                          onClick={() => onResimplify(opt.id)}
                          disabled={isProcessing}
                          title={opt.desc}
                          className={`text-[10px] py-1 px-1.5 rounded border transition-colors disabled:opacity-50 ${
                            isActive
                              ? 'border-fuchsia-400 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300'
                              : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400'
                          }`}
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                    Light is safe for any fabric. Medium/heavy trade fine
                    detail for cleaner stitching — recommended on pile
                    fabrics (velvet, fleece) and dense line-art.
                  </p>
                </div>
              )}

              <div className="mb-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Threads ({visibleColors.length})
                  </h3>
                  {hasEdits && (
                    <span className="text-[10px] text-amber-500" title="You've made manual edits">
                      ✎ edited
                    </span>
                  )}
                </div>
                {/* Brand picker — affects which thread library we match against */}
                <select
                  value={selectedBrand}
                  onChange={e => setSelectedBrand(e.target.value)}
                  className="w-full mb-2 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[10px] text-slate-600 dark:text-slate-300"
                >
                  {threadBrands.map(b => (
                    <option key={b.key} value={b.key}>{b.name} ({b.count})</option>
                  ))}
                </select>
                <div className="space-y-1.5">
                  {(detectedColors || []).map((c, idx) => {
                    const removed = removedColors.has(c.hex)
                    const swapHex = swappedColors[c.hex]
                    const match = threadMatches[idx]
                    // Display swatch reflects the current effective color
                    // (swap takes priority over the original detected hex).
                    const effectiveHex = swapHex || c.hex
                    return (
                      <div
                        key={c.hex}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all ${
                          removed
                            ? 'border-slate-200 dark:border-slate-700 opacity-40'
                            : activeColor === c.hex
                              ? 'border-blue-400 bg-blue-50 dark:bg-blue-500/10'
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <button
                          onClick={() => !removed && setActiveColor(activeColor === c.hex ? null : c.hex)}
                          disabled={removed}
                          className="w-7 h-7 rounded border-2 border-slate-300 dark:border-slate-600 flex-shrink-0"
                          style={{ backgroundColor: effectiveHex }}
                          title={swapHex
                            ? `Swapped ${c.hex} → ${swapHex}`
                            : `Use ${c.hex} for paint`}
                        />
                        <div className="flex-1 min-w-0">
                          {match && match.code ? (
                            <>
                              <a
                                href={match.amazonUrl}
                                target="_blank" rel="noopener noreferrer"
                                className="block text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline truncate"
                                title={`${match.brand} #${match.code} ${match.name} (ΔE ${match.distance})`}
                              >
                                #{match.code} {match.name}
                              </a>
                              <span className="block text-[9px] font-mono text-slate-400">
                                {(swapHex || c.hex).toUpperCase()}
                                {swapHex && (
                                  <span className="text-emerald-500"> (swapped from {c.hex.toUpperCase()})</span>
                                )}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs font-mono text-slate-700 dark:text-slate-200">
                              {(swapHex || c.hex).toUpperCase()}
                              {swapHex && (
                                <span className="text-[9px] text-emerald-500 ml-1">↔ {c.hex.toUpperCase()}</span>
                              )}
                            </span>
                          )}
                        </div>
                        {/* Color-swap button + native picker. The picker
                            stays in the DOM so its onChange still fires
                            after the user closes it. We open it via
                            ref.click() to avoid a custom popover. */}
                        {!removed && (
                          <div className="relative flex items-center">
                            <input
                              type="color"
                              value={effectiveHex.startsWith('#') ? effectiveHex : `#${effectiveHex}`}
                              onChange={e => swapColor(c.hex, e.target.value)}
                              className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
                              title="Swap this thread for another color"
                            />
                            <span
                              className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-blue-500 transition-colors pointer-events-none"
                              title="Swap this thread for another color"
                            >
                              🎨
                            </span>
                          </div>
                        )}
                        {swapHex && !removed && (
                          <button
                            onClick={() => clearSwap(c.hex)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-amber-500 transition-colors"
                            title={`Reset swap (back to ${c.hex.toUpperCase()})`}
                          >
                            ↺
                          </button>
                        )}
                        {removed ? (
                          <button
                            onClick={() => restoreColor(c.hex)}
                            className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-emerald-500 transition-colors"
                            title="Restore this color"
                          >
                            ↻
                          </button>
                        ) : (
                          <button
                            onClick={() => removeColor(c.hex)}
                            className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 transition-colors"
                            title="Remove this thread (its pixels become background)"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
                  Click a thread # to shop. Click swatch to use as Paint color.
                  🎨 swaps this thread for any other color (e.g. pewter → navy).
                  Click ✕ to remove (pixels become fabric show-through).
                </p>
              </div>

              {/* Detected text regions — per-region font picker. The cleanup
                  pipeline auto-guesses the font (serif/sans/block/mono);
                  the user can override per region before stitches generate. */}
              {detectedTextRegions && detectedTextRegions.length > 0 && (
                <div className="mb-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Text detected ({detectedTextRegions.length})
                  </h3>
                  <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50 px-2 py-1.5 mb-2">
                    <p className="text-[10px] text-emerald-700 dark:text-emerald-300 leading-relaxed">
                      <span className="font-semibold">Auto-replace ON.</span> The
                      rasterized letters in your source will be removed and
                      re-stitched with the embroidery font you pick below — at
                      the same baseline, size, and color. Pick a font per region,
                      or accept the auto-guessed default.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {detectedTextRegions.map((r, i) => {
                      const current = textFontOverrides[r.text] || r.font_guess || 'block'
                      const fonts = fontPreviews.length > 0
                        ? fontPreviews
                        : [{ key: 'block', name: 'Block' }, { key: 'serif', name: 'Serif' },
                           { key: 'sans', name: 'Sans' }, { key: 'mono', name: 'Mono' }]
                      return (
                        <div key={i} className="rounded border border-slate-200 dark:border-slate-700 p-2">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div
                              className="w-4 h-4 rounded-sm border border-slate-300 dark:border-slate-600 flex-shrink-0"
                              style={{ backgroundColor: r.color_hex || '#000' }}
                            />
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                              "{r.text}"
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {fonts.map(f => {
                              const isActive = current === f.key
                              return (
                                <button
                                  key={f.key}
                                  onClick={() => setTextFontOverrides(prev => ({ ...prev, [r.text]: f.key }))}
                                  className={`block rounded border-2 overflow-hidden transition-colors ${
                                    isActive ? 'border-blue-400' : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                  }`}
                                  title={f.name}
                                >
                                  {f.previewBase64 ? (
                                    <img
                                      src={`data:image/png;base64,${f.previewBase64}`}
                                      alt={f.name}
                                      className="w-full h-7 object-cover bg-[#f5f0e8]"
                                    />
                                  ) : (
                                    <div className="w-full h-7 flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-500">
                                      {f.name}
                                    </div>
                                  )}
                                </button>
                              )
                            })}
                          </div>
                          {r.font_guess && current !== r.font_guess && (
                            <p className="text-[9px] text-slate-400 mt-1">
                              Auto-detected: {r.font_guess}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              <div className="mb-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setShowDecisions(s => !s)}
                  className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  {showDecisions ? '▼' : '▶'} Cleanup details
                </button>
                {showDecisions && (
                  <div className="mt-2 space-y-1 text-[10px] font-mono text-slate-500 dark:text-slate-400 max-h-48 overflow-y-auto">
                    {(decisions || []).map((d, i) => (
                      <div key={i} className="leading-relaxed">{d}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer actions */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2">
              <button
                onClick={handleConfirm}
                disabled={isProcessing || visibleColors.length === 0}
                className="w-full px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold text-sm rounded-lg transition-colors"
              >
                {isProcessing ? 'Generating stitches…' : 'Confirm & generate stitches →'}
              </button>
              <button
                onClick={onCancel}
                disabled={isProcessing}
                className="w-full px-4 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ToolButton ({ active, onClick, label, icon, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded transition-colors disabled:opacity-40 ${
        active
          ? 'bg-blue-500 text-white'
          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  )
}
